"use client"

import { APP_URL, BOOK_DEMO_URL } from "../../config"
import type { FullTranslation } from "../../lib/i18n"
import { useTr } from "../../lib/use-tr"
import { LocaleLink } from "../locale-link"
import { Reveal } from "../reveal"
import { ArrowRight, CalendarClock } from "lucide-react"

import { BorderBeam } from "@/components/ui/border-beam"
import { Button } from "@/components/ui/button"

const BEAM_FROM = "oklch(0.6171 0.1375 39.0427)"
const BEAM_TO = "oklch(0.82 0.11 55)"

const CTA_BAND_T = {
  title: {
    FR: "Voyez AutoLeads travailler sur vos cibles.",
    EN: "See AutoLeads work on your targets.",
  },
  subhead: {
    FR: "15 minutes en visio : on lance une recherche sur votre marché et vous repartez avec un échantillon de leads.",
    EN: "A 15-minute call: we run a search on your market and you leave with a sample of leads.",
  },
  cta_demo: { FR: "Réserver une démo", EN: "Book a demo" },
  cta_try: { FR: "Créer un compte", EN: "Create an account" },
} satisfies FullTranslation

export function CtaBand() {
  const tr = useTr(CTA_BAND_T)

  return (
    <section className="py-16 sm:py-20">
      <Reveal className="border-border bg-card/40 relative overflow-hidden rounded-2xl border px-6 py-14 text-center sm:px-10 sm:py-16">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 60% 50% at 50% 0%, oklch(0.88 0.09 50 / 0.35), transparent 70%)",
          }}
        />
        <BorderBeam
          size={140}
          duration={7}
          colorFrom={BEAM_FROM}
          colorTo={BEAM_TO}
        />
        <BorderBeam
          size={140}
          duration={7}
          delay={3.5}
          colorFrom={BEAM_FROM}
          colorTo={BEAM_TO}
        />
        <div className="relative mx-auto max-w-2xl">
          <h2 className="text-foreground font-display leading-display tracking-dense text-3xl font-semibold text-balance sm:text-4xl">
            {tr("title")}
          </h2>
          <p className="text-muted-foreground text-md mx-auto mt-5 max-w-xl leading-relaxed text-balance sm:text-base">
            {tr("subhead")}
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <a
              href={BOOK_DEMO_URL}
              target="_blank"
              rel="noreferrer"
              className="w-full sm:w-auto"
            >
              <Button
                variant="cta"
                shape="lg"
                size="lg"
                className="cta-shine cta-glow text-md h-12 w-full px-6 font-medium sm:w-auto"
              >
                <CalendarClock className="mr-1.5 size-4" aria-hidden />
                {tr("cta_demo")}
              </Button>
            </a>
            <LocaleLink href={APP_URL} className="w-full sm:w-auto">
              <Button
                shape="lg"
                variant="soft"
                size="lg"
                className="group text-md h-12 w-full px-6 font-medium sm:w-auto"
              >
                {tr("cta_try")}
                <ArrowRight className="ml-1.5 size-4 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </LocaleLink>
          </div>
        </div>
      </Reveal>
    </section>
  )
}
